import * as React from 'react';
import { useHistory } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type OnChangeFn,
  type VisibilityState,
  type RowSelectionState,
  type PaginationState,
  type Row,
} from '@tanstack/react-table';

import { ArrowPathIcon } from '@heroicons/react/24/outline';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@dashboard/components/ui/table';
import { Button } from '@dashboard/components/ui/button';
import { Checkbox } from '@dashboard/components/ui/checkbox';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import { useDetailHref } from '@dashboard/lib/detailLinks';
import { DataGridToolbar } from './DataGridToolbar';
import { DataGridPagination } from './DataGridPagination';

// Selection column definition for row selection
function createSelectColumn<TData>(): ColumnDef<TData, unknown> {
  return {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={value => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  };
}

export interface DataGridToolbarConfig {
  /** A page heading for the toolbar row; the search, filters and actions then sit to its right. */
  title?: React.ReactNode;
  showSearch?: boolean;
  showColumnToggle?: boolean;
  searchPlaceholder?: string;
  leftActions?: React.ReactNode;
  customActions?: React.ReactNode;
  /** Contextual bar rendered above the toolbar row (e.g. bulk-action UI). */
  selectionBar?: React.ReactNode;
}

export interface DataGridProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  isLoading?: boolean;
  paginationMode?: 'infinite' | 'page';
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /**
   * Boolean toggles selection on every row; a function lets the caller
   * gate per-row (e.g. "already-invited" rows in the past-events
   * disclosure). Passes straight through to react-table.
   */
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  enableColumnVisibility?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  /**
   * Total page count for server-driven pagination. When set, react-table
   * runs in `manualPagination` mode — it trusts the supplied `data` to
   * already be a single page and uses `pageCount` to render footer
   * controls. Omit to let react-table compute pagination from the local
   * data array.
   */
  pageCount?: number;
  pageSize?: number;
  /** Skeleton rows while loading, when the list is known to be shorter than a page. */
  loadingRows?: number;
  pageSizeOptions?: number[];
  emptyMessage?: string;
  emptyState?: React.ReactNode;
  className?: string;
  toolbar?: DataGridToolbarConfig;
  /**
   * Stable row identifier (e.g. row => row._id). Without this, react-table
   * falls back to array index, which causes selection state to drift when
   * the data is filtered, sorted, or paginated. Provide it whenever a
   * domain id is available — required for rowSelection to behave correctly
   * across re-renders.
   */
  getRowId?: (row: TData, index: number) => string;
  /**
   * When provided, makes the entire row clickable and navigates to the
   * returned path. Clicks on interactive descendants (links, buttons, inputs,
   * checkboxes) are ignored. Cmd/Ctrl-click and middle-click open the row in
   * a new tab.
   */
  getRowHref?: (row: TData) => string | undefined;
  /** More router state for the record a row opens, such as where its back link returns. */
  rowLinkState?: Record<string, unknown>;
  /**
   * When provided, rows can be expanded to reveal additional content beneath
   * them (spanning all columns). Trigger expansion from a column cell via
   * `row.getToggleExpandedHandler()` or `row.toggleExpanded()`.
   */
  renderSubRow?: (row: Row<TData>) => React.ReactNode;
}

// Ignore row navigation if the click originated on an interactive element
// (link, button, input, checkbox, etc.). This keeps inner links, action
// buttons, and row selection checkboxes working as expected.
function isInteractiveClickTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return !!target.closest(
    'a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="menuitem"], [data-no-row-nav]',
  );
}

// On a phone the columns keep their content on one line and the table scrolls sideways,
// instead of squeezing a column down to a letter per line. A cell's own max width would let the
// unwrapped text spill into the next column, so it is lifted too. The customer cell (max-w-xs)
// is the exception: a long email would fill the phone and push amount and status off screen.
const MOBILE_TABLE_CLASS =
  'max-md:[&_td]:whitespace-nowrap max-md:[&_th]:whitespace-nowrap max-md:[&_td_div]:max-w-none max-md:[&_td_.max-w-xs]:max-w-44 max-md:[&_td_.max-w-xs>*]:truncate';

export function DataGrid<TData>({
  columns,
  data,
  isLoading = false,
  paginationMode = 'infinite',
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sorting,
  onSortingChange,
  enableRowSelection = false,
  enableColumnVisibility = false,
  rowSelection,
  onRowSelectionChange,
  columnVisibility,
  onColumnVisibilityChange,
  globalFilter: globalFilterProp,
  onGlobalFilterChange,
  pagination: paginationProp,
  onPaginationChange,
  pageCount,
  pageSize = 10,
  loadingRows,
  pageSizeOptions,
  emptyMessage = 'No data',
  emptyState,
  className,
  toolbar,
  getRowId,
  getRowHref,
  rowLinkState,
  renderSubRow,
}: DataGridProps<TData>) {
  const history = useHistory();
  const toDetail = useDetailHref();
  // Internal state for uncontrolled global filter
  const [internalGlobalFilter, setInternalGlobalFilter] = React.useState('');

  // Internal state for uncontrolled pagination
  const [internalPagination, setInternalPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });

  // Use controlled or uncontrolled state for global filter
  const isControlledFilter = globalFilterProp !== undefined;
  const globalFilter = isControlledFilter ? globalFilterProp : internalGlobalFilter;

  // Use controlled or uncontrolled state for pagination
  const isControlledPagination = paginationProp !== undefined;
  const pagination = isControlledPagination ? paginationProp : internalPagination;

  // Prepend select column when row selection is enabled
  const allColumns = React.useMemo(() => {
    if (enableRowSelection) {
      return [createSelectColumn<TData>(), ...columns];
    }
    return columns;
  }, [columns, enableRowSelection]);

  // Feature flags — only enable row models and handlers that a given usage
  // actually needs. Wiring up controlled handlers (onPaginationChange,
  // onGlobalFilterChange, etc.) without the matching controlled state can
  // cause react-table to repeatedly fire reconciliation updates.
  const sortingEnabled = sorting !== undefined || onSortingChange !== undefined;
  const filteringEnabled = globalFilterProp !== undefined || !!toolbar?.showSearch;
  const paginationEnabled = paginationMode === 'page';

  // When the caller passes a pageCount, react-table runs in manual mode:
  // it skips its own pagination row model and trusts `data` to be a
  // single page. `manualSorting` + `manualFiltering` get the same
  // treatment when the caller is driving sort/filter server-side.
  const isManualPagination = pageCount !== undefined;

  const table = useReactTable({
    data,
    columns: allColumns,
    ...(getRowId && { getRowId }),
    getCoreRowModel: getCoreRowModel(),
    ...(sortingEnabled && !onGlobalFilterChange && { getSortedRowModel: getSortedRowModel() }),
    ...(filteringEnabled &&
      !onGlobalFilterChange && { getFilteredRowModel: getFilteredRowModel() }),
    ...(paginationEnabled &&
      !isManualPagination && { getPaginationRowModel: getPaginationRowModel() }),
    ...(isManualPagination && { manualPagination: true, pageCount }),
    ...(onGlobalFilterChange && { manualFiltering: true }),
    ...(renderSubRow && {
      getExpandedRowModel: getExpandedRowModel(),
      getRowCanExpand: () => true,
    }),
    manualSorting: !!onSortingChange,
    ...(sortingEnabled && { onSortingChange }),
    onRowSelectionChange,
    onColumnVisibilityChange,
    ...(filteringEnabled && {
      onGlobalFilterChange: (updater => {
        const newValue = typeof updater === 'function' ? updater(globalFilter) : updater;
        if (onGlobalFilterChange) {
          onGlobalFilterChange(newValue);
        } else {
          setInternalGlobalFilter(newValue);
        }
      }) as OnChangeFn<string>,
    }),
    ...(paginationEnabled && {
      onPaginationChange: (updater => {
        const newValue = typeof updater === 'function' ? updater(pagination) : updater;
        if (onPaginationChange) {
          onPaginationChange(newValue);
        } else {
          setInternalPagination(newValue);
        }
      }) as OnChangeFn<PaginationState>,
    }),
    state: {
      rowSelection: rowSelection ?? {},
      columnVisibility: columnVisibility ?? {},
      ...(sortingEnabled && { sorting: sorting ?? [] }),
      ...(filteringEnabled && { globalFilter }),
      ...(paginationEnabled && { pagination }),
    },
    enableRowSelection,
    enableSorting: sortingEnabled,
    enableGlobalFilter: filteringEnabled,
  });

  const renderTableContent = () => (
    <>
      <TableHeader>
        {table.getHeaderGroups().map(headerGroup => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <TableHead
                key={header.id}
                className={
                  (header.column.columnDef.meta as { className?: string } | undefined)?.className
                }
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading && data.length === 0 ? (
          Array.from({ length: loadingRows ?? pageSize }).map((_, rowIndex) => (
            <TableRow key={`skeleton-${rowIndex}`} aria-hidden="true">
              {allColumns.map((_col, colIndex) => (
                <TableCell key={colIndex}>
                  <Skeleton className="h-4 w-full max-w-[200px]" />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : table.getRowModel().rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={allColumns.length} className="h-24 text-center">
              {globalFilter ? (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <span>No results found</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => table.setGlobalFilter('')}
                  >
                    Clear search
                  </Button>
                </div>
              ) : (
                <span className="text-muted-foreground">{emptyMessage}</span>
              )}
            </TableCell>
          </TableRow>
        ) : (
          table.getRowModel().rows.map(row => {
            const href = getRowHref ? toDetail(getRowHref(row.original)) : undefined;
            const canExpandRow = !!renderSubRow;
            const navigate = href
              ? (openInNewTab: boolean) => {
                  if (openInNewTab) {
                    window.open(
                      history.createHref({ pathname: href }),
                      '_blank',
                      'noopener,noreferrer',
                    );
                  } else {
                    // The detail's back link returns to this list with its filters.
                    history.push(href, {
                      from: `${history.location.pathname}${history.location.search}`,
                      ...rowLinkState,
                    });
                  }
                }
              : undefined;
            const activate = navigate
              ? (openInNewTab: boolean) => navigate(openInNewTab)
              : canExpandRow
                ? () => row.toggleExpanded()
                : undefined;
            const isInteractive = !!activate;
            const handleRowClick = activate
              ? (e: React.MouseEvent<HTMLTableRowElement>) => {
                  if (isInteractiveClickTarget(e.target)) return;
                  activate(e.metaKey || e.ctrlKey);
                }
              : undefined;
            const handleRowAuxClick = navigate
              ? (e: React.MouseEvent<HTMLTableRowElement>) => {
                  if (e.button !== 1) return;
                  if (isInteractiveClickTarget(e.target)) return;
                  navigate(true);
                }
              : undefined;
            const handleRowKeyDown = activate
              ? (e: React.KeyboardEvent<HTMLTableRowElement>) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  if (isInteractiveClickTarget(e.target)) return;
                  e.preventDefault();
                  activate(e.metaKey || e.ctrlKey);
                }
              : undefined;
            return (
              <React.Fragment key={row.id}>
                <TableRow
                  data-state={row.getIsSelected() && 'selected'}
                  className={
                    isInteractive
                      ? 'cursor-pointer outline-none focus-visible:bg-muted/60'
                      : undefined
                  }
                  tabIndex={isInteractive ? 0 : undefined}
                  role={href ? 'link' : undefined}
                  aria-expanded={canExpandRow ? row.getIsExpanded() : undefined}
                  onClick={handleRowClick}
                  onAuxClick={handleRowAuxClick}
                  onKeyDown={handleRowKeyDown}
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      key={cell.id}
                      className={
                        (cell.column.columnDef.meta as { className?: string } | undefined)
                          ?.className
                      }
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
                {renderSubRow && row.getIsExpanded() && (
                  <TableRow data-state="expanded">
                    <TableCell colSpan={row.getVisibleCells().length} className="p-0">
                      {renderSubRow(row)}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })
        )}
        {isFetchingNextPage && (
          <TableRow aria-hidden="true">
            <TableCell
              colSpan={allColumns.length}
              className="py-4 text-center text-muted-foreground"
            >
              <span className="inline-flex items-center gap-2">
                <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
                Loading more
              </span>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </>
  );

  const renderToolbar = () => {
    if (!toolbar) return null;
    return (
      <DataGridToolbar
        table={table}
        title={toolbar.title}
        showSearch={toolbar.showSearch}
        showColumnToggle={toolbar.showColumnToggle}
        searchPlaceholder={toolbar.searchPlaceholder}
        leftActions={toolbar.leftActions}
        customActions={toolbar.customActions}
        selectionBar={toolbar.selectionBar}
      />
    );
  };

  // Custom empty state rendered outside the table
  const isEmpty = !isLoading && data.length === 0;
  if (emptyState && isEmpty) {
    return (
      <div className={className}>
        {renderToolbar()}
        <div className="rounded-md border border-border" aria-busy={isLoading || undefined}>
          {emptyState}
        </div>
      </div>
    );
  }

  // Infinite scroll mode
  if (paginationMode === 'infinite' && fetchNextPage) {
    return (
      <div className={className}>
        {renderToolbar()}
        <div className="rounded-md border border-border" aria-busy={isLoading || undefined}>
          <BottomScrollListener
            onBottom={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            isLoadingMore={isFetchingNextPage}
            itemCount={data.length}
          >
            <Table className={MOBILE_TABLE_CLASS}>{renderTableContent()}</Table>
          </BottomScrollListener>
        </div>
      </div>
    );
  }

  // Page mode with pagination controls
  if (paginationMode === 'page') {
    return (
      <div className={className}>
        {renderToolbar()}
        <div className="rounded-md border border-border" aria-busy={isLoading || undefined}>
          <Table className={MOBILE_TABLE_CLASS}>{renderTableContent()}</Table>
        </div>
        <DataGridPagination table={table} pageSizeOptions={pageSizeOptions} />
      </div>
    );
  }

  // Default mode (no pagination controls)
  return (
    <div className={className}>
      {renderToolbar()}
      <div className="rounded-md border border-gray-200 dark:border-border">
        <Table className={MOBILE_TABLE_CLASS}>{renderTableContent()}</Table>
      </div>
    </div>
  );
}

export default DataGrid;
